const CARTESIAN = new Set(['bar', 'scatter', 'scattergl']);
const TYPES = [
  ['auto', 'Auto'],
  ['bar', 'Bar'],
  ['hbar', 'Bar (horizontal)'],
  ['line', 'Line'],
  ['area', 'Area'],
  ['stacked', 'Stacked bar'],
  ['scatter', 'Scatter'],
  ['pie', 'Donut']
];
const TYPE_CYCLE = ['auto', 'bar', 'hbar', 'line', 'area', 'scatter', 'pie'];
const SORTS = [['none', 'Original order'], ['desc', 'Largest first'], ['asc', 'Smallest first'], ['alpha', 'A to Z']];
const LIMITS = [['0', 'All'], ['5', 'Top 5'], ['10', 'Top 10'], ['20', 'Top 20'], ['50', 'Top 50']];
const FORMATS = [
  ['auto', 'Auto'],
  ['si', '1.2k'],
  ['comma', '1,234'],
  ['dec2', '1,234.56'],
  ['pct', '12%'],
  ['usd', '$1,234']
];
const FORMAT_SPEC = {
  si: { tick: '~s', label: '%{v:.3~s}' },
  comma: { tick: ',.0f', label: '%{v:,.0f}' },
  dec2: { tick: ',.2f', label: '%{v:,.2f}' },
  pct: { tick: '.0%', label: '%{v:.1%}' },
  usd: { tick: '$,.0f', label: '$%{v:,.0f}' }
};
const PALETTES = {
  helix: ['#6d3fd1', '#1f9e89', '#e0782b', '#d0457c', '#3b82c4', '#9a8c1f', '#7a5c4a', '#5b6475'],
  tableau: ['#4e79a7', '#f28e2b', '#e15759', '#76b7b2', '#59a14f', '#edc948', '#b07aa1', '#ff9da7'],
  okabe: ['#0072b2', '#e69f00', '#009e73', '#cc79a7', '#56b4e9', '#d55e00', '#f0e442', '#000000'],
  mono: ['#4c2a99', '#6d3fd1', '#9370e6', '#b9a3f0', '#d9cdf7', '#3a2275', '#7f5fd9', '#a68bea']
};
const THEMES = [['default', 'Default'], ['helix', 'Clean'], ['dark', 'Dark'], ['print', 'Print']];
const APP_FONT = 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';

const clone = v => (v === undefined ? v : JSON.parse(JSON.stringify(v)));

export function isCartesian(data) {
  return Array.isArray(data) && data.length > 0 && data.every(t => CARTESIAN.has(String((t && t.type) || 'scatter')));
}

function isHorizontal(trace) {
  return trace && trace.type === 'bar' && trace.orientation === 'h';
}

function catKey(trace) {
  return isHorizontal(trace) ? 'y' : 'x';
}

function valKey(trace) {
  return isHorizontal(trace) ? 'x' : 'y';
}

function isCategorical(values) {
  if (!Array.isArray(values) || !values.length) return false;
  return values.some(v => typeof v === 'string' && v.trim() !== '' && Number.isNaN(Number(v)) && Number.isNaN(Date.parse(v)));
}

function filterTrace(trace, keep) {
  const n = (trace[catKey(trace)] || []).length;
  const pick = arr => arr.filter((_, i) => keep[i]);
  Object.keys(trace).forEach(k => {
    if (Array.isArray(trace[k]) && trace[k].length === n) trace[k] = pick(trace[k]);
  });
  ['marker', 'error_y', 'error_x'].forEach(obj => {
    const o = trace[obj];
    if (!o) return;
    Object.keys(o).forEach(k => {
      if (Array.isArray(o[k]) && o[k].length === n) o[k] = pick(o[k]);
    });
  });
}

function reorderTrace(trace, order) {
  const ck = catKey(trace);
  const cats = trace[ck] || [];
  const n = cats.length;
  const idx = cats.map((c, i) => i).sort((a, b) => order.get(String(cats[a])) - order.get(String(cats[b])));
  const apply = arr => idx.map(i => arr[i]);
  Object.keys(trace).forEach(k => {
    if (Array.isArray(trace[k]) && trace[k].length === n) trace[k] = apply(trace[k]);
  });
  if (trace.marker) {
    Object.keys(trace.marker).forEach(k => {
      if (Array.isArray(trace.marker[k]) && trace.marker[k].length === n) trace.marker[k] = apply(trace.marker[k]);
    });
  }
}

function categoryTotals(traces) {
  const totals = new Map();
  traces.forEach(t => {
    const cats = t[catKey(t)] || [];
    const vals = t[valKey(t)] || [];
    cats.forEach((c, i) => {
      const k = String(c);
      const v = Number(vals[i]);
      totals.set(k, (totals.get(k) || 0) + (Number.isFinite(v) ? v : 0));
    });
  });
  return totals;
}

export function sortAndLimit(traces, sort, limit) {
  if (!traces.length || !isCategorical(traces[0][catKey(traces[0])])) return traces;
  const totals = categoryTotals(traces);
  let ranked = Array.from(totals.keys());
  if (sort === 'desc' || (limit > 0 && sort === 'none')) ranked.sort((a, b) => totals.get(b) - totals.get(a));
  else if (sort === 'asc') ranked.sort((a, b) => totals.get(a) - totals.get(b));
  else if (sort === 'alpha') ranked.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
  if (limit > 0) {
    const top = new Set(ranked.slice(0, limit));
    if (sort === 'none') {
      const firstSeen = [];
      traces.forEach(t => (t[catKey(t)] || []).forEach(c => { const k = String(c); if (top.has(k) && !firstSeen.includes(k)) firstSeen.push(k); }));
      ranked = firstSeen;
    } else {
      ranked = ranked.slice(0, limit);
    }
    traces.forEach(t => filterTrace(t, (t[catKey(t)] || []).map(c => top.has(String(c)))));
  }
  if (sort !== 'none') {
    const order = new Map(ranked.map((k, i) => [k, i]));
    traces.forEach(t => reorderTrace(t, order));
  }
  return traces;
}

function labelTemplate(fmt, axis) {
  const spec = FORMAT_SPEC[fmt];
  const tpl = spec ? spec.label : '%{v:.3~s}';
  return tpl.replace('v', axis);
}

function swapAxes(trace) {
  const x = trace.x;
  trace.x = trace.y;
  trace.y = x;
  ['xaxis', 'yaxis'].forEach(k => delete trace[k]);
}

export function transformFigure(original, state) {
  const data = clone(original.data || []);
  const layout = clone(original.layout || {});
  const s = state || {};
  const cart = isCartesian(data);
  let out = data;
  let horizontalOut = cart && data.every(isHorizontal);

  if (cart) {
    sortAndLimit(out, s.sort || 'none', Number(s.limit || 0));
    const type = s.type || 'auto';
    if (type === 'pie' && out.length) {
      const t = out[0];
      const totals = categoryTotals(out);
      out = [{
        type: 'pie',
        labels: Array.from(totals.keys()),
        values: Array.from(totals.values()),
        hole: 0.45,
        sort: false,
        textinfo: 'percent',
        name: t.name || ''
      }];
      horizontalOut = false;
    } else if (type !== 'auto') {
      const wantH = type === 'hbar';
      out.forEach((t, i) => {
        const wasH = isHorizontal(t);
        if (wasH && !wantH) { swapAxes(t); delete t.orientation; }
        if (!wasH && wantH) { swapAxes(t); }
        delete t.fill;
        delete t.stackgroup;
        if (type === 'bar' || type === 'hbar' || type === 'stacked') {
          t.type = 'bar';
          delete t.mode;
          t.orientation = wantH ? 'h' : 'v';
        } else {
          t.type = 'scatter';
          delete t.orientation;
          if (type === 'line') t.mode = 'lines+markers';
          if (type === 'scatter') t.mode = 'markers';
          if (type === 'area') { t.mode = 'lines'; t.fill = out.length > 1 ? (i === 0 ? 'tozeroy' : 'tonexty') : 'tozeroy'; t.stackgroup = out.length > 1 ? 'one' : undefined; if (!t.stackgroup) delete t.stackgroup; }
        }
      });
      if (wantH !== horizontalOut) {
        const xa = layout.xaxis;
        layout.xaxis = layout.yaxis;
        layout.yaxis = xa;
        if (!layout.xaxis) delete layout.xaxis;
        if (!layout.yaxis) delete layout.yaxis;
      }
      horizontalOut = wantH;
      if (type === 'stacked') layout.barmode = 'stack';
      else if (type === 'bar' || type === 'hbar') layout.barmode = layout.barmode === 'stack' ? 'group' : layout.barmode;
      if (wantH) {
        layout.yaxis = Object.assign({}, layout.yaxis, { automargin: true });
        if (s.sort && s.sort !== 'none') layout.yaxis.autorange = 'reversed';
      }
    }
    if (s.labels && out[0] && out[0].type !== 'pie') {
      out.forEach(t => {
        const axis = isHorizontal(t) ? 'x' : 'y';
        t.texttemplate = labelTemplate(s.format, axis);
        if (t.type === 'bar') { t.textposition = 'outside'; t.cliponaxis = false; }
        else { t.mode = (t.mode || 'lines').includes('text') ? t.mode : `${t.mode || 'lines'}+text`; t.textposition = 'top center'; }
      });
    }
  }

  if (out[0] && out[0].type === 'pie') {
    delete layout.xaxis;
    delete layout.yaxis;
    if (s.labels) out[0].textinfo = 'label+percent';
  }

  const valueAxis = horizontalOut ? 'xaxis' : 'yaxis';
  const catAxis = horizontalOut ? 'yaxis' : 'xaxis';
  if (cart && out[0] && out[0].type !== 'pie') {
    if (s.logY) layout[valueAxis] = Object.assign({}, layout[valueAxis], { type: 'log' });
    if (s.logX) layout[catAxis] = Object.assign({}, layout[catAxis], { type: 'log' });
    const spec = FORMAT_SPEC[s.format];
    if (spec) layout[valueAxis] = Object.assign({}, layout[valueAxis], { tickformat: spec.tick });
    if (s.grid === false) {
      layout.xaxis = Object.assign({}, layout.xaxis, { showgrid: false });
      layout.yaxis = Object.assign({}, layout.yaxis, { showgrid: false });
    }
    if (s.xTitle != null) layout[catAxis] = Object.assign({}, layout[catAxis], { title: { text: s.xTitle } });
    if (s.yTitle != null) layout[valueAxis] = Object.assign({}, layout[valueAxis], { title: { text: s.yTitle } });
  }

  if (s.legend === true || s.legend === false) layout.showlegend = s.legend;
  if (s.title != null) {
    const prev = typeof layout.title === 'object' && layout.title ? layout.title : {};
    layout.title = Object.assign({}, prev, { text: s.title });
  }

  const pal = PALETTES[s.palette];
  if (pal) {
    layout.colorway = pal.slice();
    out.forEach((t, i) => {
      if (t.type === 'pie') { t.marker = Object.assign({}, t.marker, { colors: t.labels.map((_, j) => pal[j % pal.length]) }); return; }
      const c = pal[i % pal.length];
      t.marker = Object.assign({}, t.marker);
      if (!Array.isArray(t.marker.color) || out.length > 1) t.marker.color = c;
      if (t.type !== 'bar') t.line = Object.assign({}, t.line, { color: c });
    });
  }

  applyTheme(layout, s.theme);
  if (s.height) layout.height = s.height;
  return { data: out, layout };
}

function applyTheme(layout, theme) {
  if (!theme || theme === 'default') return;
  const axis = (k, extra) => { layout[k] = Object.assign({}, layout[k], extra); };
  if (theme === 'helix' || theme === 'print') {
    layout.font = Object.assign({}, layout.font, { family: APP_FONT, color: '#2b2238' });
    layout.paper_bgcolor = '#ffffff';
    layout.plot_bgcolor = '#ffffff';
    const grid = theme === 'print' ? '#d9d9d9' : '#ece8f3';
    axis('xaxis', { gridcolor: grid, linecolor: '#c9c2d6', zerolinecolor: grid });
    axis('yaxis', { gridcolor: grid, linecolor: '#c9c2d6', zerolinecolor: grid });
    if (theme === 'print') layout.font.color = '#000000';
  } else if (theme === 'dark') {
    layout.font = Object.assign({}, layout.font, { family: APP_FONT, color: '#e8e3f3' });
    layout.paper_bgcolor = '#15121c';
    layout.plot_bgcolor = '#15121c';
    axis('xaxis', { gridcolor: '#2e2840', linecolor: '#4a4060', zerolinecolor: '#2e2840' });
    axis('yaxis', { gridcolor: '#2e2840', linecolor: '#4a4060', zerolinecolor: '#2e2840' });
    if (layout.legend) layout.legend = Object.assign({}, layout.legend, { bgcolor: 'rgba(0,0,0,0)' });
  }
}

const csvCell = v => {
  if (v == null) return '';
  const s = typeof v === 'object' ? JSON.stringify(v) : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export function figureToCsv(data) {
  const traces = Array.isArray(data) ? data : [];
  if (!traces.length) return '';
  if (isCartesian(traces) && traces.length > 1) {
    const ck = catKey(traces[0]);
    const cats = [];
    const seen = new Set();
    traces.forEach(t => (t[catKey(t)] || []).forEach(c => { const k = String(c); if (!seen.has(k)) { seen.add(k); cats.push(c); } }));
    const cols = traces.map((t, i) => t.name || `series_${i + 1}`);
    const maps = traces.map(t => {
      const m = new Map();
      (t[catKey(t)] || []).forEach((c, i) => m.set(String(c), (t[valKey(t)] || [])[i]));
      return m;
    });
    const rows = [[ck, ...cols].map(csvCell).join(',')];
    cats.forEach(c => rows.push([c, ...maps.map(m => m.get(String(c)))].map(csvCell).join(',')));
    return rows.join('\n');
  }
  const rows = [];
  traces.forEach((t, ti) => {
    const name = t.name || `series_${ti + 1}`;
    if (t.type === 'pie') {
      if (!rows.length) rows.push('series,label,value');
      (t.labels || []).forEach((l, i) => rows.push([name, l, (t.values || [])[i]].map(csvCell).join(',')));
      return;
    }
    const keys = ['x', 'y', 'z', 'open', 'high', 'low', 'close', 'lat', 'lon', 'text'].filter(k => Array.isArray(t[k]));
    if (!keys.length) return;
    if (Array.isArray(t.z) && Array.isArray(t.z[0])) {
      if (!rows.length) rows.push('series,x,y,z');
      t.z.forEach((row, yi) => row.forEach((z, xi) => rows.push([name, (t.x || [])[xi] ?? xi, (t.y || [])[yi] ?? yi, z].map(csvCell).join(','))));
      return;
    }
    if (!rows.length) rows.push(['series', ...keys].join(','));
    const n = Math.max(...keys.map(k => t[k].length));
    for (let i = 0; i < n; i += 1) rows.push([name, ...keys.map(k => t[k][i])].map(csvCell).join(','));
  });
  return rows.join('\n');
}

function slug(text) {
  return (String(text || 'helix-chart').toLowerCase().replace(/<[^>]+>/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 60)) || 'helix-chart';
}

function download(name, mime, content) {
  const blob = content instanceof Blob ? content : new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function toast(message) {
  if (typeof window.showSnackbar === 'function') { try { window.showSnackbar(message); return; } catch (_) {} }
  let el = document.getElementById('hx-toast');
  if (!el) {
    el = document.createElement('div');
    el.id = 'hx-toast';
    el.setAttribute('role', 'status');
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add('is-visible');
  clearTimeout(el._t);
  el._t = setTimeout(() => el.classList.remove('is-visible'), 2200);
}

function titleText(layout) {
  const t = layout && layout.title;
  if (!t) return '';
  return String(typeof t === 'string' ? t : t.text || '').replace(/<[^>]+>/g, '');
}

const registry = new Set();
let activeChart = null;

function isEditable(el) {
  if (!el) return false;
  const tag = el.tagName ? el.tagName.toLowerCase() : '';
  return tag === 'input' || tag === 'textarea' || tag === 'select' || el.isContentEditable;
}

function option(list, value) {
  return list.map(([v, l]) => `<option value="${v}"${String(v) === String(value) ? ' selected' : ''}>${l}</option>`).join('');
}

function btn(action, icon, title, extra = '') {
  return `<button type="button" class="hx-tb-btn" data-hx="${action}" title="${title}" aria-label="${title}"${extra}><span class="material-icons" aria-hidden="true">${icon}</span></button>`;
}

class ChartControls {
  constructor(gd) {
    this.gd = gd;
    this.state = {};
    this.original = null;
    this.rendered = null;
    this.busy = false;
    this.container = gd.closest('.chat-felix-chart') || gd.closest('.grid-stack-item-content') || gd.parentElement;
    this.inGrid = !!gd.closest('.grid-stack-item-content');
    this.snapshot();
    this.build();
    gd.classList.add('hx-chart-enhanced');
    gd.addEventListener('pointerenter', () => { activeChart = this; });
    gd.addEventListener('focusin', () => { activeChart = this; });
    if (typeof gd.on === 'function') gd.on('plotly_afterplot', () => {
      this.onAfterPlot();
      if (this.inGrid && !this.busy) { clearTimeout(this.fitTimer); this.fitTimer = setTimeout(() => this.fitGrid(), 200); }
    });
    registry.add(this);
    activeChart = this;
  }

  snapshot() {
    this.original = { data: clone(this.gd.data || []), layout: clone(this.gd.layout || {}) };
    const fixes = fontFixes(this.original.layout);
    if (Object.keys(fixes).length && window.Plotly) {
      try { Promise.resolve(window.Plotly.relayout(this.gd, fixes)).catch(() => {}); } catch (_) {}
      this.original.layout = clone(this.gd.layout || {});
    }
    delete this.original.layout.height;
    if (this.gd.layout && this.gd.layout.height && !this.gd._hxDefaultHeight) this.original.layout.height = this.gd.layout.height;
    this.rendered = this.gd.data;
  }

  onAfterPlot() {
    if (this.busy || this.gd.data === this.rendered) return;
    this.snapshot();
    this.state = {};
    this.sync();
  }

  destroy() {
    if (this.ro) this.ro.disconnect();
    if (this.toolbar) this.toolbar.remove();
    if (this.grip) this.grip.remove();
    if (this.fullscreen) this.toggleFullscreen(false);
    this.gd.classList.remove('hx-chart-enhanced');
    delete this.gd._hxControls;
    registry.delete(this);
    if (activeChart === this) activeChart = null;
  }

  get graphId() {
    return String(this.gd.id || '').replace(/graph-target$/, '');
  }

  build() {
    const cart = isCartesian(this.gd.data);
    const bar = document.createElement('div');
    bar.className = this.inGrid ? 'hx-chart-toolbar hx-chart-toolbar--overlay' : 'hx-chart-toolbar';
    bar.setAttribute('role', 'toolbar');
    bar.setAttribute('aria-label', 'Chart controls');
    const s = this.state;
    bar.innerHTML = [
      cart ? `<label class="hx-tb-field" title="Chart type (t)"><span class="material-icons" aria-hidden="true">insert_chart_outlined</span><select data-hx="type" aria-label="Chart type">${option(TYPES, s.type || 'auto')}</select></label>` : '',
      cart ? `<label class="hx-tb-field" title="Sort (s)"><span class="material-icons" aria-hidden="true">sort</span><select data-hx="sort" aria-label="Sort">${option(SORTS, s.sort || 'none')}</select></label>` : '',
      cart ? `<label class="hx-tb-field" title="Limit categories"><span class="material-icons" aria-hidden="true">filter_list</span><select data-hx="limit" aria-label="Limit">${option(LIMITS, s.limit || '0')}</select></label>` : '',
      cart ? '<span class="hx-tb-sep"></span>' : '',
      cart ? btn('logY', 'stacked_line_chart', 'Log scale (l)', ' aria-pressed="false"') : '',
      cart ? btn('labels', 'pin', 'Data labels (n)', ' aria-pressed="false"') : '',
      btn('legend', 'legend_toggle', 'Toggle legend', ' aria-pressed="false"'),
      cart ? btn('grid', 'grid_on', 'Toggle gridlines', ' aria-pressed="true"') : '',
      btn('format', 'tune', 'Format: title, axes, numbers, colors', ' aria-haspopup="true"'),
      '<span class="hx-tb-spacer"></span>',
      btn('export', 'download', 'Export (e)', ' aria-haspopup="true"'),
      this.inGrid ? '' : btn('share', 'link', 'Share link'),
      this.inGrid ? '' : btn('dashboard', 'add_chart', 'Add to dashboard'),
      this.inGrid ? btn('wide', 'width_full', 'Full-width tile', ' aria-pressed="false"') : '',
      this.inGrid ? btn('tall', 'height', 'Tall tile', ' aria-pressed="false"') : '',
      btn('reset', 'restart_alt', 'Reset chart (r)'),
      this.inGrid ? '' : btn('fullscreen', 'fullscreen', 'Fullscreen (f)')
    ].join('');
    this.toolbar = bar;
    this.gd.parentNode.insertBefore(bar, this.gd);
    bar.addEventListener('change', e => this.onChange(e));
    bar.addEventListener('click', e => this.onClick(e));
    bar.addEventListener('pointerenter', () => { activeChart = this; });
    if (!this.inGrid) {
      const grip = document.createElement('div');
      grip.className = 'hx-chart-resize';
      grip.title = 'Drag to resize, double-click to reset height';
      grip.setAttribute('aria-hidden', 'true');
      this.gd.parentNode.insertBefore(grip, this.gd.nextSibling);
      this.grip = grip;
      grip.addEventListener('pointerdown', e => this.startResize(e));
      grip.addEventListener('dblclick', () => this.setState({ height: undefined }));
    }
    if (this.inGrid && typeof ResizeObserver !== 'undefined') {
      this.ro = new ResizeObserver(() => {
        clearTimeout(this.fitTimer);
        this.fitTimer = setTimeout(() => this.fitGrid(), 120);
      });
      this.ro.observe(this.container);
      ['wide', 'tall'].forEach(kind => {
        let saved = false;
        try { saved = localStorage.getItem(this.tileKey(kind)) === '1'; } catch (_) {}
        if (saved) this.toggleTile(kind, true);
      });
      setTimeout(() => this.fitGrid(), 50);
    }
    this.sync();
  }

  tileKey(kind) {
    return `hx.tile.${kind}.${this.graphId}`;
  }

  toggleTile(kind, force) {
    const item = this.gd.closest('.grid-stack-item');
    if (!item) return;
    const on = force != null ? force : !item.classList.contains(`hx-tile-${kind}`);
    item.classList.toggle(`hx-tile-${kind}`, on);
    const b = this.toolbar.querySelector(`[data-hx="${kind}"]`);
    if (b) b.setAttribute('aria-pressed', on ? 'true' : 'false');
    if (force == null) {
      try { if (on) localStorage.setItem(this.tileKey(kind), '1'); else localStorage.removeItem(this.tileKey(kind)); } catch (_) {}
    }
    setTimeout(() => { try { Promise.resolve(window.Plotly.Plots.resize(this.gd)).catch(() => {}); } catch (_) {} this.fitGrid(); }, 60);
  }

  fitGrid() {
    const box = this.container;
    if (!box || !this.gd.isConnected || !box.offsetHeight || this.fullscreen) return;
    let trailing = 0;
    let node = this.gd;
    while (node && node !== box) {
      let sib = node.nextElementSibling;
      while (sib) {
        const st = getComputedStyle(sib);
        if (st.position !== 'absolute' && st.position !== 'fixed' && st.display !== 'none') trailing += sib.offsetHeight;
        sib = sib.nextElementSibling;
      }
      node = node.parentElement;
    }
    const avail = Math.floor(box.getBoundingClientRect().bottom - this.gd.getBoundingClientRect().top - trailing - 6);
    const h = Math.max(160, avail);
    const L = this.gd.layout || {};
    const update = {};
    const cur = L.height;
    if (!cur || Math.abs(cur - h) >= 6) update.height = h;
    const title = titleText(L);
    const width = this.gd.offsetWidth || box.offsetWidth;
    if (title && width) {
      const base = this.titleSize || (L.title && L.title.font && L.title.font.size) || 17;
      this.titleSize = base;
      const fit = Math.max(11, Math.min(base, Math.floor((width - 24) / (title.length * 0.56))));
      const now = L.title && L.title.font && L.title.font.size;
      if (fit !== now) update['title.font.size'] = fit;
    }
    if (!Object.keys(update).length) return;
    if (update.height) this.gd._hxDefaultHeight = h;
    if (window.Plotly) {
      this.busy = true;
      Promise.resolve(window.Plotly.relayout(this.gd, update))
        .catch(() => {})
        .finally(() => { this.rendered = this.gd.data; this.busy = false; });
    }
  }

  sync() {
    const s = this.state;
    const q = sel => this.toolbar.querySelector(sel);
    const setSel = (k, v) => { const el = q(`select[data-hx="${k}"]`); if (el) el.value = v; };
    setSel('type', s.type || 'auto');
    setSel('sort', s.sort || 'none');
    setSel('limit', String(s.limit || 0));
    const press = (k, on) => { const el = q(`[data-hx="${k}"]`); if (el) el.setAttribute('aria-pressed', on ? 'true' : 'false'); };
    press('logY', !!s.logY);
    press('labels', !!s.labels);
    const legendOn = s.legend != null ? s.legend : this.gd.layout && this.gd.layout.showlegend !== false && (this.gd.data || []).length > 1;
    press('legend', legendOn);
    press('grid', s.grid !== false);
  }

  setState(patch) {
    Object.assign(this.state, patch);
    Object.keys(this.state).forEach(k => this.state[k] === undefined && delete this.state[k]);
    return this.render();
  }

  async render() {
    const Plotly = window.Plotly;
    if (!Plotly) return;
    if (this.gd.data !== this.rendered) this.snapshot();
    const fig = transformFigure(this.original, this.state);
    if (!fig.layout.height && this.gd._hxDefaultHeight) fig.layout.height = this.gd._hxDefaultHeight;
    if (this.fullscreen) fig.layout.height = this.fullHeight();
    this.busy = true;
    try {
      await Plotly.react(this.gd, fig.data, fig.layout, this.gd._context ? { ...pickConfig(this.gd._context) } : undefined);
    } catch (err) {
      console.warn('chart controls render failed', err);
      toast('That option does not apply to this chart.');
    } finally {
      this.rendered = this.gd.data;
      this.busy = false;
    }
    this.sync();
  }

  onChange(e) {
    const k = e.target.getAttribute('data-hx');
    if (k === 'type') this.setState({ type: e.target.value === 'auto' ? undefined : e.target.value });
    if (k === 'sort') this.setState({ sort: e.target.value === 'none' ? undefined : e.target.value });
    if (k === 'limit') this.setState({ limit: Number(e.target.value) || undefined });
  }

  onClick(e) {
    const b = e.target.closest('[data-hx]');
    if (!b || b.tagName === 'SELECT' || b.tagName === 'LABEL') return;
    const k = b.getAttribute('data-hx');
    const legendOn = () => (this.state.legend != null ? this.state.legend : this.gd.layout && this.gd.layout.showlegend !== false && (this.gd.data || []).length > 1);
    if (k === 'logY') this.setState({ logY: !this.state.logY || undefined });
    else if (k === 'labels') this.setState({ labels: !this.state.labels || undefined });
    else if (k === 'legend') this.setState({ legend: !legendOn() });
    else if (k === 'grid') this.setState({ grid: this.state.grid === false ? undefined : false });
    else if (k === 'wide' || k === 'tall') this.toggleTile(k);
    else if (k === 'reset') { this.state = {}; this.render(); }
    else if (k === 'fullscreen') this.toggleFullscreen();
    else if (k === 'export') this.menu(b, this.exportMenu());
    else if (k === 'format') this.menu(b, this.formatMenu());
    else if (k === 'share') this.share();
    else if (k === 'dashboard') this.addToDashboard();
  }

  menu(anchor, html) {
    closeMenus();
    const m = document.createElement('div');
    m.className = 'hx-chart-menu';
    m.setAttribute('role', 'menu');
    m.innerHTML = html;
    document.body.appendChild(m);
    const r = anchor.getBoundingClientRect();
    const w = m.offsetWidth;
    m.style.top = `${Math.min(window.innerHeight - m.offsetHeight - 8, r.bottom + 4)}px`;
    m.style.left = `${Math.max(8, Math.min(window.innerWidth - w - 8, r.right - w))}px`;
    m.addEventListener('click', ev => {
      const it = ev.target.closest('[data-act]');
      if (!it) return;
      this.menuAction(it.getAttribute('data-act'), it.getAttribute('data-val'), m);
    });
    m.addEventListener('change', ev => {
      const it = ev.target.closest('[data-set]');
      if (it) this.setState({ [it.getAttribute('data-set')]: it.value === '' || it.value === 'auto' || it.value === 'default' ? undefined : it.value });
    });
    m.addEventListener('keydown', ev => {
      if (ev.key === 'Escape') { closeMenus(); anchor.focus(); }
      if (ev.key === 'Enter' && ev.target.matches('input[data-text]')) { ev.preventDefault(); this.applyText(m); closeMenus(); }
    });
    const first = m.querySelector('input,select,button');
    if (first) first.focus();
  }

  exportMenu() {
    const items = [
      ['png', 'image', 'PNG image (2x)'],
      ['svg', 'polyline', 'SVG vector'],
      ['csv', 'table_view', 'CSV data'],
      ['json', 'data_object', 'Plotly JSON'],
      ['copy', 'content_copy', 'Copy image']
    ];
    return items.map(([a, i, l]) => `<button type="button" role="menuitem" data-act="${a}"><span class="material-icons" aria-hidden="true">${i}</span>${l}</button>`).join('');
  }

  formatMenu() {
    const s = this.state;
    const cart = isCartesian(this.original.data);
    const L = this.gd.layout || {};
    const axTitle = ax => { const t = L[ax] && L[ax].title; return t ? String(typeof t === 'string' ? t : t.text || '') : ''; };
    const esc = v => String(v || '').replace(/"/g, '&quot;');
    const h = !!(this.gd.data || []).every(isHorizontal) && cart;
    return `<div class="hx-menu-form">
      <label>Title<input data-text="title" value="${esc(s.title != null ? s.title : titleText(L))}"></label>
      ${cart ? `<label>X axis<input data-text="${h ? 'yTitle' : 'xTitle'}" value="${esc(axTitle('xaxis'))}"></label>
      <label>Y axis<input data-text="${h ? 'xTitle' : 'yTitle'}" value="${esc(axTitle('yaxis'))}"></label>
      <label>Numbers<select data-set="format">${option(FORMATS, s.format || 'auto')}</select></label>` : ''}
      <label>Theme<select data-set="theme">${option(THEMES, s.theme || 'default')}</select></label>
      <label>Colors<select data-set="palette">${option([['', 'Original'], ['helix', 'Helix'], ['tableau', 'Tableau 10'], ['okabe', 'Colorblind safe'], ['mono', 'Single hue']], s.palette || '')}</select></label>
      ${cart ? `<label class="hx-check"><input type="checkbox" data-act="logX"${s.logX ? ' checked' : ''}>Log X axis</label>` : ''}
      <div class="hx-menu-actions"><button type="button" data-act="apply-text" class="hx-primary">Apply</button></div>
    </div>`;
  }

  applyText(m) {
    const patch = {};
    m.querySelectorAll('input[data-text]').forEach(inp => { patch[inp.getAttribute('data-text')] = inp.value; });
    this.setState(patch);
  }

  async menuAction(act, _val, m) {
    const Plotly = window.Plotly;
    const name = slug(titleText(this.gd.layout) || this.graphId);
    const size = { width: Math.max(800, this.gd.offsetWidth || 0), height: Math.max(450, this.gd.offsetHeight || 0) };
    if (act === 'apply-text') { this.applyText(m); closeMenus(); return; }
    if (act === 'logX') { this.setState({ logX: !this.state.logX || undefined }); return; }
    closeMenus();
    try {
      if (act === 'png') await Plotly.downloadImage(this.gd, { format: 'png', scale: 2, filename: name, ...size });
      else if (act === 'svg') await Plotly.downloadImage(this.gd, { format: 'svg', filename: name, ...size });
      else if (act === 'csv') download(`${name}.csv`, 'text/csv', figureToCsv(this.gd.data));
      else if (act === 'json') download(`${name}.json`, 'application/json', JSON.stringify({ data: this.gd.data, layout: this.gd.layout }, null, 2));
      else if (act === 'copy') {
        const url = await Plotly.toImage(this.gd, { format: 'png', scale: 2, ...size });
        const blob = await (await fetch(url)).blob();
        if (navigator.clipboard && window.ClipboardItem) {
          await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': blob })]);
          toast('Chart image copied');
        } else {
          download(`${name}.png`, 'image/png', blob);
        }
      }
    } catch (err) {
      console.warn('chart export failed', err);
      toast('Export failed. Try PNG or CSV.');
    }
  }

  share() {
    const id = this.graphId;
    const el = document.querySelector(`.share-chat-message-icon[data-id="${id}"], .panel-share-button[data-id="${id}"]`);
    if (el) { el.click(); return; }
    if (typeof window.openShareDialog === 'function') { window.openShareDialog(id); return; }
    toast('Sharing is not available for this chart.');
  }

  queryText() {
    const id = this.graphId;
    const icon = document.querySelector(`.share-chat-message-icon[data-id="${id}"]`);
    const msg = icon && icon.parentElement && icon.parentElement.querySelector('.chat-log__message');
    if (msg && msg.textContent.trim()) return msg.textContent.trim();
    let prev = this.gd.closest('.chat-log__item');
    while (prev && (prev = prev.previousElementSibling)) {
      const m = prev.querySelector && prev.querySelector('.chat-log__item--own .chat-log__message');
      if (m) return m.textContent.trim();
    }
    return titleText(this.gd.layout);
  }

  async addToDashboard() {
    const da = window.dataAccessor;
    const user = window.userData;
    const query = this.queryText();
    if (!da || !user || !query) { toast('Sign in to save charts to a dashboard.'); return; }
    try {
      let name = da.currentActiveDashboardName;
      if (!name && typeof da.getCurrentActiveDashboardName === 'function') name = await da.getCurrentActiveDashboardName(user);
      name = name || 'Untitled';
      await da.addGraphToDashboard(user, name, Math.random().toString(36).slice(2, 9), query);
      toast(`Added to dashboard "${name}"`);
    } catch (err) {
      console.warn('add to dashboard failed', err);
      toast('Could not add to dashboard.');
    }
  }

  fullHeight() {
    return Math.max(240, window.innerHeight - (this.toolbar ? this.toolbar.offsetHeight : 40) - 24);
  }

  toggleFullscreen(force) {
    const on = force != null ? force : !this.fullscreen;
    if (on === !!this.fullscreen) return;
    this.fullscreen = on;
    this.container.classList.toggle('hx-chart-fullscreen', on);
    document.body.classList.toggle('hx-has-fullscreen-chart', on);
    const b = this.toolbar.querySelector('[data-hx="fullscreen"] .material-icons');
    if (b) b.textContent = on ? 'fullscreen_exit' : 'fullscreen';
    if (on) activeChart = this;
    const Plotly = window.Plotly;
    if (!Plotly) return;
    const height = on ? this.fullHeight() : this.state.height || this.gd._hxDefaultHeight || this.original.layout.height || null;
    requestAnimationFrame(() => {
      this.busy = true;
      Promise.resolve(Plotly.relayout(this.gd, { height, width: null, autosize: true }))
        .catch(() => {})
        .finally(() => { this.rendered = this.gd.data; this.busy = false; });
    });
  }

  startResize(e) {
    e.preventDefault();
    const startY = e.clientY;
    const startH = this.gd.offsetHeight;
    const grip = e.currentTarget;
    grip.setPointerCapture(e.pointerId);
    grip.classList.add('is-dragging');
    let h = startH;
    let raf = 0;
    const move = ev => {
      h = Math.max(220, Math.min(2400, startH + ev.clientY - startY));
      if (!raf) raf = requestAnimationFrame(() => { raf = 0; this.gd.style.height = `${h}px`; });
    };
    const up = () => {
      grip.removeEventListener('pointermove', move);
      grip.removeEventListener('pointerup', up);
      grip.classList.remove('is-dragging');
      this.gd.style.height = '';
      this.setState({ height: Math.round(h) });
    };
    grip.addEventListener('pointermove', move);
    grip.addEventListener('pointerup', up);
  }

  cycleType() {
    const cur = this.state.type || 'auto';
    const next = TYPE_CYCLE[(TYPE_CYCLE.indexOf(cur) + 1) % TYPE_CYCLE.length];
    return this.setState({ type: next === 'auto' ? undefined : next });
  }

  cycleSort() {
    const order = ['none', 'desc', 'asc', 'alpha'];
    const next = order[(order.indexOf(this.state.sort || 'none') + 1) % order.length];
    return this.setState({ sort: next === 'none' ? undefined : next });
  }
}

function pickConfig(ctx) {
  const out = {};
  ['responsive', 'displaylogo', 'scrollZoom', 'modeBarButtonsToRemove', 'modeBarButtonsToAdd', 'displayModeBar', 'staticPlot'].forEach(k => {
    if (ctx[k] !== undefined) out[k] = ctx[k];
  });
  return out;
}

function closeMenus() {
  document.querySelectorAll('.hx-chart-menu').forEach(m => m.remove());
}

function defaultChartHeight() {
  const vh = window.innerHeight || 800;
  if ((window.innerWidth || 1024) <= 640) return Math.round(Math.max(280, Math.min(420, vh * 0.48)));
  return Math.round(Math.max(380, Math.min(860, vh * 0.58)));
}

const GENERIC_FONT = /(sans-serif|serif|monospace|system-ui|cursive|fantasy)\s*$/i;

export function fontFixes(layout) {
  const out = {};
  const walk = (obj, path, depth) => {
    if (!obj || typeof obj !== 'object' || depth > 4) return;
    if (Array.isArray(obj)) { obj.forEach((v, i) => walk(v, `${path}[${i}]`, depth + 1)); return; }
    Object.keys(obj).forEach(k => {
      if (k === 'template') return;
      const p = path ? `${path}.${k}` : k;
      const v = obj[k];
      if (k === 'family' && typeof v === 'string' && v.trim() && (!GENERIC_FONT.test(v.trim()) || /courier\s*,\s*monospace\s*$/i.test(v))) {
        const base = v.replace(/\s*,?\s*courier\s*,\s*monospace\s*$/i, '');
        out[p] = `${base}, ${APP_FONT}`;
      } else if (typeof v === 'object') walk(v, p, depth + 1);
    });
  };
  walk(layout, '', 0);
  return out;
}

function eligible(gd) {
  if (!gd || gd._hxControls || typeof gd.closest !== 'function' || typeof gd.querySelector !== 'function' || !gd.closest('#app-shell-layout')) return false;
  if (gd.querySelector('.js-plotly-plot')) return false;
  if (!Array.isArray(gd.data) || !gd.data.length || !gd.parentNode) return false;
  return true;
}

function attach(gd) {
  if (!eligible(gd)) return;
  const inChat = !!gd.closest('.chat-felix-chart');
  const update = fontFixes(gd.layout || {});
  const serverH = gd.layout && Number(gd.layout.height);
  if (inChat && gd.layout && (!serverH || serverH <= 500)) {
    const h = Math.max(serverH || 0, defaultChartHeight());
    gd._hxDefaultHeight = h;
    if (h !== serverH) update.height = h;
  }
  if (window.Plotly && Object.keys(update).length) {
    try { Promise.resolve(window.Plotly.relayout(gd, update)).catch(() => {}); } catch (_) {}
  }
  gd._hxControls = new ChartControls(gd);
}

function scan(root) {
  registry.forEach(c => { if (!c.gd.isConnected || c.gd.querySelector('.js-plotly-plot')) c.destroy(); });
  const scope = root && root.querySelectorAll ? root : document;
  if (scope.classList && scope.classList.contains('js-plotly-plot')) attach(scope);
  scope.querySelectorAll('.js-plotly-plot').forEach(attach);
}

function liveCharts() {
  return Array.from(registry).filter(c => c.gd.isConnected);
}

function pickActive() {
  const live = liveCharts();
  if (activeChart && activeChart.gd.isConnected) return activeChart;
  const vh = window.innerHeight;
  return live.find(c => { const r = c.gd.getBoundingClientRect(); return r.bottom > 0 && r.top < vh; }) || live[live.length - 1] || null;
}

function moveActive(delta) {
  const live = liveCharts();
  if (!live.length) return;
  const i = Math.max(0, live.indexOf(pickActive()));
  const next = live[Math.max(0, Math.min(live.length - 1, i + delta))];
  activeChart = next;
  next.gd.scrollIntoView({ behavior: 'smooth', block: 'center' });
  live.forEach(c => c.container.classList.toggle('hx-chart-active', c === next));
}

const SHORTCUTS = [
  ['/', 'Focus the question box'],
  ['Ctrl+K', 'Focus the question box'],
  ['Ctrl+Enter', 'Run question'],
  ['[', 'Collapse or expand sidebar'],
  ['j / k', 'Next / previous chart'],
  ['f', 'Fullscreen active chart'],
  ['Esc', 'Exit fullscreen, close menus'],
  ['t', 'Cycle chart type'],
  ['s', 'Cycle sort order'],
  ['l', 'Toggle log scale'],
  ['n', 'Toggle data labels'],
  ['e', 'Export menu'],
  ['r', 'Reset chart'],
  ['Ctrl+Shift+S', 'Open SQL editor'],
  ['?', 'Show shortcuts']
];

function showShortcuts() {
  let d = document.getElementById('keyboard-shortcuts-dialog');
  if (d && !d.dataset.hx) { d.remove(); d = null; }
  if (!d) {
    d = document.createElement('dialog');
    d.id = 'keyboard-shortcuts-dialog';
    d.dataset.hx = '1';
    d.className = 'keyboard-shortcuts-dialog hx-shortcuts';
    d.innerHTML = `<h3>Keyboard shortcuts</h3><table>${SHORTCUTS.map(([k, v]) => `<tr><td><kbd>${k}</kbd></td><td>${v}</td></tr>`).join('')}</table><div class="dialog-close-row"><button type="button" class="mdl-button" data-close>Close</button></div>`;
    d.querySelector('[data-close]').addEventListener('click', () => d.close());
    d.addEventListener('click', e => { if (e.target === d) d.close(); });
    document.body.appendChild(d);
  }
  if (d.open) d.close();
  else if (typeof d.showModal === 'function') d.showModal();
}

const SIDEBAR_KEY = 'hx.sidebarCollapsed';

function setSidebar(collapsed) {
  document.body.classList.toggle('hx-sidebar-collapsed', collapsed);
  document.querySelectorAll('.hx-sidebar-toggle').forEach(b => {
    b.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    b.title = collapsed ? 'Show sidebar ([)' : 'Hide sidebar ([)';
    const i = b.querySelector('.material-icons');
    if (i) i.textContent = collapsed ? 'menu' : 'menu_open';
  });
  try { localStorage.setItem(SIDEBAR_KEY, collapsed ? '1' : '0'); } catch (_) {}
  setTimeout(() => liveCharts().forEach(c => { try { Promise.resolve(window.Plotly.Plots.resize(c.gd)).catch(() => {}); } catch (_) {} }), 60);
}

function installSidebarToggle() {
  const content = document.querySelector('#app-shell-layout > .mdl-layout__content');
  if (!content || document.querySelector('.hx-sidebar-toggle')) return;
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'hx-sidebar-toggle';
  b.innerHTML = '<span class="material-icons" aria-hidden="true">menu_open</span>';
  b.setAttribute('aria-controls', 'app-shell-drawer');
  b.addEventListener('click', () => setSidebar(!document.body.classList.contains('hx-sidebar-collapsed')));
  content.insertBefore(b, content.firstChild);
  let saved = false;
  try { saved = localStorage.getItem(SIDEBAR_KEY) === '1'; } catch (_) {}
  setSidebar(saved);
}

function installModelToggle() {
  const controls = document.querySelector('.helix-model-controls');
  const row = document.querySelector('#askfelixform .speech-input-row');
  if (!controls || !row || document.querySelector('.hx-model-toggle')) return;
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'hx-model-toggle';
  b.title = 'Model settings';
  b.setAttribute('aria-label', 'Model settings');
  b.setAttribute('aria-expanded', 'false');
  b.innerHTML = '<span class="material-icons" aria-hidden="true">tune</span>';
  b.addEventListener('click', () => {
    const open = !document.body.classList.contains('hx-model-open');
    document.body.classList.toggle('hx-model-open', open);
    b.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
  row.appendChild(b);
}

function onKey(e) {
  if (e.defaultPrevented || e.altKey || e.metaKey) return;
  if (e.key === 'Escape') {
    if (document.querySelector('.hx-chart-menu')) { closeMenus(); return; }
    const fs = liveCharts().find(c => c.fullscreen);
    if (fs) { e.preventDefault(); fs.toggleFullscreen(false); }
    return;
  }
  if (e.ctrlKey || isEditable(document.activeElement)) return;
  if (document.querySelector('dialog[open]:not(#keyboard-shortcuts-dialog)')) return;
  const k = e.key;
  if (k === '?') { e.preventDefault(); e.stopImmediatePropagation(); showShortcuts(); return; }
  if (k === '/') {
    const q = document.getElementById('query');
    if (q) { e.preventDefault(); q.focus(); }
    return;
  }
  if (k === '[') { if (document.getElementById('app-shell-drawer')) { e.preventDefault(); setSidebar(!document.body.classList.contains('hx-sidebar-collapsed')); } return; }
  if (k === 'j' || k === 'k') { e.preventDefault(); moveActive(k === 'j' ? 1 : -1); return; }
  const c = pickActive();
  if (!c) return;
  const cart = isCartesian(c.original.data);
  const map = {
    f: () => !c.inGrid && c.toggleFullscreen(),
    t: () => cart && c.cycleType(),
    s: () => cart && c.cycleSort(),
    l: () => cart && c.setState({ logY: !c.state.logY || undefined }),
    n: () => cart && c.setState({ labels: !c.state.labels || undefined }),
    r: () => { c.state = {}; c.render(); },
    e: () => { const b = c.toolbar.querySelector('[data-hx="export"]'); if (b) c.menu(b, c.exportMenu()); }
  };
  if (map[k]) { e.preventDefault(); map[k](); }
}

export function initChartControls() {
  if (typeof window === 'undefined' || typeof document === 'undefined' || window.__hxChartControls) return;
  window.__hxChartControls = true;
  const start = () => {
    const shell = document.getElementById('app-shell-layout');
    if (!shell || typeof MutationObserver === 'undefined' || typeof shell.querySelectorAll !== 'function' || typeof window.addEventListener !== 'function') return;
    document.body.classList.add('hx-analyst');
    installSidebarToggle();
    installModelToggle();
    scan(document);
    let pending = false;
    const mo = new MutationObserver(muts => {
      if (pending) return;
      if (!muts.some(m => m.type === 'attributes' || m.addedNodes.length)) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; scan(document); });
    });
    mo.observe(document.getElementById('app-shell-layout'), { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    window.addEventListener('keydown', onKey, true);
    document.addEventListener('pointerdown', e => { if (!e.target.closest('.hx-chart-menu, [data-hx="export"], [data-hx="format"]')) closeMenus(); });
    window.addEventListener('resize', () => liveCharts().filter(c => c.fullscreen).forEach(c => { try { Promise.resolve(window.Plotly.relayout(c.gd, { height: c.fullHeight() })).catch(() => {}); } catch (_) {} }));
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
}

if (typeof window !== 'undefined') window.helixChartControls = { transformFigure, figureToCsv, sortAndLimit, fontFixes, showShortcuts };
initChartControls();
